import { z } from 'zod';
/**
 * Canvas / Artifact 패널에 렌더되는 산출물의 종류.
 * 프런트는 kind 별로 다른 렌더러를 사용 (iframe sandbox / mermaid / markdown 등).
 * `react` 는 DEPRECATED — 생성은 거부되며, 기존 DB 레코드 읽기용 legacy 허용만 남긴다.
 */
export const ArtifactKindSchema = z.enum(['html', 'svg', 'mermaid', 'markdown', 'code']);
/** 조회 전용 — 기존 DB 에 남아있을 수 있는 legacy 'react' 까지 허용. 신규 생성에는 쓰지 말 것. */
export const ArtifactKindLegacySchema = z.enum([
    'html',
    'svg',
    'mermaid',
    'markdown',
    'code',
    'react',
]);
/** 본문을 제외한 버전 메타. 목록 조회 / 버전 네비에 사용. */
export const ArtifactVersionMetaSchema = z.object({
    version: z.number().int().positive(),
    changeNote: z.string().nullable(),
    createdByMessageId: z.string().uuid().nullable(),
    createdAt: z.string(),
});
/** 본문 포함 — 단일 버전 fetch 응답. */
export const ArtifactVersionSchema = ArtifactVersionMetaSchema.extend({
    artifactId: z.string().uuid(),
    content: z.string(),
    /**
     * 본문에 embed 된 queryId 중 saved_queries 에서 이미 사라진 것들 (관리자가 삭제하거나
     * 사용자가 대화를 지워 cascade 로 떨어진 경우 등). 하나라도 비어있지 않으면 프런트는
     * "대시보드가 만료되었습니다" 오버레이를 띄운다. 누락 시 빈 배열로 간주.
     */
    expiredQueryIds: z.array(z.string().uuid()).optional(),
});
/** Artifact head — 본문 미포함, 가벼운 목록용. legacy 'react' 도 허용해 기존 기록이 깨지지 않게. */
export const ArtifactSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    kind: ArtifactKindLegacySchema,
    title: z.string(),
    currentVersion: z.number().int().positive(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Artifact head + 모든 버전 메타 (본문 X). */
export const ArtifactDetailSchema = ArtifactSchema.extend({
    versions: z.array(ArtifactVersionMetaSchema),
});
export const ArtifactListSchema = z.object({
    artifacts: z.array(ArtifactSchema),
});
/**
 * `system.create_artifact` 툴의 LLM 입력 zod 스키마 — registry 와 동일하게 유지.
 *
 * `kind` / `title` 는 LLM 에게는 required 로 안내하지만 (openaiFunction.parameters 에 required[]),
 * 누락 시 각각 'html' / '콘텐츠' 로 자동 보정한다. 시스템 프롬프트 길이로 인해 모델이 가끔
 * 이 필드들을 빠뜨려 무한 retry 에 빠지는 회귀를 막는다 (kind: 2026-05-08, title: 2026-05-14).
 */
export const CreateArtifactToolInputSchema = z.object({
    kind: ArtifactKindSchema.optional().default('html'),
    title: z.string().min(1).max(200).optional().default('콘텐츠'),
    content: z.string().min(1).max(200_000),
    summary: z.string().max(500).optional(),
});
/** `system.update_artifact` 툴의 LLM 입력 zod 스키마. */
export const UpdateArtifactToolInputSchema = z.object({
    artifactId: z.string().uuid(),
    content: z.string().min(1).max(200_000),
    changeNote: z.string().min(1).max(500),
});
/** 툴 호출의 output (LLM 회신용 + chat.ts 가 SSE event 로 변환할 메타). */
export const CreateArtifactToolOutputSchema = z.object({
    artifactId: z.string().uuid(),
    kind: ArtifactKindSchema,
    title: z.string(),
    version: z.literal(1),
    summary: z.string().nullable(),
});
export const UpdateArtifactToolOutputSchema = z.object({
    artifactId: z.string().uuid(),
    version: z.number().int().positive(),
    changeNote: z.string(),
});
/**
 * iframe artifact 가 `window.artifact.*` 로 호출할 수 있는 메서드 목록.
 * 서버 라우트에서 각 메서드를 기존 SkillToolKey 로 매핑해 `runTool()` 재사용.
 * `web.fetch_url` 은 의도적으로 제외 — iframe 이 임의 외부 URL 호출할 경로를 주지 않기 위함.
 * `query` / `excelQuery` 는 제거됨 — SQL 을 artifact srcdoc 에 인라인하지 못하게 하고,
 * 대신 사전에 저장된 SQL 의 `queryId` 만 받는 `runSavedQuery` 로 대체.
 */
export const ArtifactRpcMethodSchema = z.enum([
    'runSavedQuery',
    'listTables',
    'describeTable',
    'excelList',
    'excelDescribe',
    'excelHead',
]);
/**
 * `runSavedQuery` 메서드의 params. 서버는 queryId 의 소유권을 검증한 뒤 저장된 SQL 을 실행한다.
 *
 * `params` 는 저장된 SQL 안의 `:name` 형태 placeholder 를 런타임에 바인딩한다.
 *  - 값은 string | number | boolean | null 만 허용 (배열/객체 금지).
 *  - 키는 `[a-zA-Z_][a-zA-Z0-9_]*` 패턴.
 *  - 서버가 `:name` → `$N` 으로 안전하게 치환하고 driver 의 parameterized query 로 실행한다.
 *  - SQL 문자열 안에 raw 값이 들어가지 않으므로 SQL injection 안전.
 */
export const ArtifactRunSavedQueryParamsSchema = z.object({
    queryId: z.string().uuid(),
    maxRows: z.number().int().min(1).max(10_000).optional(),
    params: z
        .record(z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, {
        message: '파라미터 이름은 [a-zA-Z_][a-zA-Z0-9_]* 패턴이어야 합니다.',
    }), z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]))
        .optional(),
});
/** `POST /api/artifacts/:id/rpc` 요청 body. `params` 는 대상 toolKey 의 input 스키마로 서버에서 재검증. */
export const ArtifactRpcRequestSchema = z.object({
    method: ArtifactRpcMethodSchema,
    params: z.unknown().optional(),
});
export const ArtifactRpcResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), data: z.unknown() }),
    z.object({
        ok: z.literal(false),
        error: z.string(),
        /** 'query_expired' — 저장된 쿼리가 이미 삭제되어 찾을 수 없음. 프런트는 대시보드 만료 오버레이로 전환. */
        code: z.string().optional(),
    }),
]);
//# sourceMappingURL=artifacts.js.map